import { Link } from "react-router-dom";
import { useState, useRef, useLayoutEffect, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import { ArrowRight, Sparkles, Image as ImageIcon, FileDown, Layers, Wand2, Lock, Check, X, Crown, Mail, Dices, Music2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { THEME_PAGES, themePageUrl } from "../lib/themePages";
import BingoBall from "../components/BingoBall";
import BingoCardRender from "../components/BingoCardRender";
import { SINTERKLAAS_DECK } from "../lib/sinterklaasDeck";
import { getTheme, toRenderTheme } from "../lib/themes";
import { trackEvent } from "../lib/analytics";
import ProPlusModal from "../components/ProPlusModal";
import WhyBingoMaker from "../components/WhyBingoMaker";
import SmartFindBar from "../components/SmartFindBar";
import PaywallModal from "../components/PaywallModal";
import MobilePlayPreview from "../components/MobilePlayPreview";
import UseCases from "../components/UseCases";
import { toast } from "sonner";

// Hero visual — renders REAL bingo cards (the exact PDF renderer) so
// visitors see precisely how their cards come out: readable text (AutoFitText),
// true theme colours and a logo + brand name on the front card to show the
// branding feature. A small sample brand emblem (crisp transparent SVG).
const SAMPLE_LOGO =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'>" +
      "<circle cx='50' cy='50' r='46' fill='#FFCC00' stroke='#111' stroke-width='6'/>" +
      "<circle cx='50' cy='50' r='29' fill='#ffffff' stroke='#111' stroke-width='4'/>" +
      "<circle cx='24' cy='30' r='4' fill='#FF3B30'/>" +
      "<circle cx='78' cy='68' r='4' fill='#2E7D32'/>" +
      "<text x='50' y='64' font-family='Outfit,DM Sans,sans-serif' font-size='38' font-weight='900' text-anchor='middle' fill='#FF3B30'>B</text>" +
      "</svg>"
  );

// Map a list of 24 words/songs to 25 renderer cells with a centred FREE space
// (index 12). No empty cells: pass exactly 24 items.
const heroCells = (arr) => {
  const items = arr.slice(0, 24);
  return Array.from({ length: 25 }, (_, i) => {
    if (i === 12) return { text: "", image: null, isFree: true };
    const idx = i < 12 ? i : i - 1;
    return { text: items[idx] || "", image: null, isFree: false };
  });
};

const HERO_KERST = heroCells(["Boom","Slee","Engel","Ster","Kaars","Piek","Bal","Hulst","Cadeau","Lint","IJs","Stol","Muts","Sneeuw","Haard","Krans","Ree","Diner","Vlok","Kaart","Lied","Stal","Bel","Warm"]);
// Music bingo shown the way it really prints: "Artiest | Titel".
const HERO_MUZIEK = heroCells(["ABBA | SOS","Queen | Radio","Nirvana | Bloom","Adele | Hello","Beatles | Help!","Toto | Africa","Prince | Kiss","Journey | Lights","Coldplay | Yellow","Madonna | Frozen","U2 | One","Blur | Song 2","Kinks | Lola","Pink | So What","Lorde | Royals","Bowie | Heroes","Sia | Alive","Rihanna | Work","Bee Gees | Words","Cher | Strong","Elton | Daniel","Roxette | Look","Blondie | Atomic","Police | King"]);
// Sinterklaas "plaatjesbingo" — real picture card (illustrations + names) so
// visitors instantly see that picture cards exist, not only word cards.
const HERO_SINT_PIC = SINTERKLAAS_DECK.slice(0, 25).map((d) => ({ text: d.nl, image: d.image, isFree: false }));

const HERO_THEME_KERST = toRenderTheme(getTheme("kerst"));
const HERO_THEME_MUZIEK = toRenderTheme(getTheme("muziek"));
const HERO_THEME_SINT = toRenderTheme(getTheme("sinterklaas"));

// Renders children at a fixed design width and scales them down to fill the
// parent slot — keeps the real card crisp & readable while fitting the collage.
function HeroCard({ designWidth = 660, children }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.45);
  const [boxH, setBoxH] = useState(0);
  useLayoutEffect(() => {
    const compute = () => {
      const c = ref.current;
      if (!c) return;
      const cw = c.clientWidth;
      const s = cw > 0 ? cw / designWidth : 0.45;
      setScale(s);
      const inner = c.firstElementChild;
      if (inner) setBoxH(inner.scrollHeight * s);
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, [designWidth]);
  return (
    <div ref={ref} style={{ width: "100%", height: boxH, position: "relative" }}>
      <div style={{ width: designWidth, transformOrigin: "top left", transform: `scale(${scale})`, position: "absolute", top: 0, left: 0 }}>
        {children}
      </div>
    </div>
  );
}

export default function Landing() {
  const { t, lang } = useI18n();
  // Bingo-caller promo banner copy (NL/EN/DE) — kept inline to avoid touching
  // the shared i18n file for a single marketing block.
  const callerBanner = ({
    nl: { badge: "Gratis bingo caller", h: "De mooiste bingo caller — nu op groot scherm", p: "Trek de ballen op tv of beamer en bedien alles vanaf je telefoon. Met omroepstem in 20 talen.", cta: "Open de gratis caller", f1: "Tweede scherm: tv toont, telefoon bedient", f2: "Omroepstem (spraak) in elke taal", f3: "Internationaal · 20 wereldtalen", f4: "75 & 90 ballen · gratis · offline" },
    en: { badge: "Free bingo caller", h: "The best bingo caller — now on the big screen", p: "Draw the balls on your TV or beamer and control everything from your phone. With a caller voice in 20 languages.", cta: "Open the free caller", f1: "Second screen: TV shows, phone controls", f2: "Caller voice (speech) in every language", f3: "International · 20 world languages", f4: "75 & 90 balls · free · offline" },
    de: { badge: "Kostenloser Bingo-Caller", h: "Der schönste Bingo-Caller — jetzt auf dem großen Bildschirm", p: "Ziehe die Kugeln auf TV oder Beamer und steuere alles per Handy. Mit Ansagerstimme in 20 Sprachen.", cta: "Kostenlosen Caller öffnen", f1: "Zweiter Bildschirm: TV zeigt, Handy steuert", f2: "Ansagerstimme (Sprache) in jeder Sprache", f3: "International · 20 Weltsprachen", f4: "75 & 90 Kugeln · gratis · offline" },
  })[lang] || null;
  const [showProPlus, setShowProPlus] = useState(false);
  // Landing "Activate PRO" now opens the tiered paywall modal so users can
  // pick their pricing tier (50/100/unlimited cards) instead of being sent
  // straight to a €9,95 Stripe checkout. Bugfix 2026-07-05.
  const [showPaywall, setShowPaywall] = useState(false);

  const handleActivatePro = () => { trackEvent("upgrade_clicked", { source: "landing_pricing" }); setShowPaywall(true); };
  // FASE F — fire pricing_viewed once when the pricing section scrolls in.
  useEffect(() => {
    const el = document.querySelector("[data-testid='pricing-comparison']");
    if (!el) return;
    let fired = false;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !fired) { fired = true; trackEvent("pricing_viewed", {}); io.disconnect(); }
      });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const FEATURES = [
    { icon: Layers, title: t("f1_title"), desc: t("f1_desc") },
    { icon: ImageIcon, title: t("f2_title"), desc: t("f2_desc") },
    { icon: Wand2, title: t("f3_title"), desc: t("f3_desc") },
    { icon: FileDown, title: t("f4_title"), desc: t("f4_desc") },
  ];

  // Hero card mockup uses MockBingoCard component defined below

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Root SEO canonical + hreflang alternates — moved out of
          index.html (2026-07-09) so per-route Helmet can control them
          without duplicating the tag on SPA sub-routes. */}
      <Helmet>
        {/* SEO title + meta description — 2026-08-16 rewrite targeting
            money keywords per GSC data. Homepage ranked #26 for
            "bingo card generator" (1047 impressions). New title puts
            the exact-match keyword first for both NL & EN visitors. */}
        <title>{
          lang === "en"
            ? "Bingo Card Generator — Create Unlimited Printable Bingo Cards"
            : lang === "de"
              ? "Bingokarten Generator — Unbegrenzt druckbare Bingokarten erstellen"
              : "Bingokaarten Generator — Onbeperkt printbare bingokaarten maken"
        }</title>
        <meta name="description" content={
          lang === "en"
            ? "Free bingo card generator. Create unlimited printable bingo cards in 30 seconds — 3x3, 4x4 or 5x5, 15 themes, own text or photos. Print or play online. No account."
            : lang === "de"
              ? "Kostenloser Bingokarten Generator. Erstelle unbegrenzt druckbare Bingokarten in 30 Sekunden — 3x3, 4x4 oder 5x5, 15 Themen, eigener Text oder Fotos. Drucken oder online spielen."
              : "Gratis bingokaarten generator. Maak in 30 seconden onbeperkt printbare bingokaarten — 3x3, 4x4 of 5x5, 15 thema's, eigen tekst of foto's. Print of speel online. Geen account."
        } />
        <link rel="canonical" href="https://bingocards-generator.com/" />
        <link rel="alternate" hrefLang="nl" href="https://bingocards-generator.com/" />
        <link rel="alternate" hrefLang="en" href="https://bingocards-generator.com/?lang=en" />
        <link rel="alternate" hrefLang="de" href="https://bingocards-generator.com/?lang=de" />
        <link rel="alternate" hrefLang="x-default" href="https://bingocards-generator.com/" />
      </Helmet>
      <Header />

      {/* Seasonal Sinterklaas promo — auto-shows Oct–Dec (or ?sint=1 to preview). */}
      {(() => {
        const m = new Date().getMonth();
        const forced = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("sint") === "1";
        if (lang !== "nl") return null;
        if (!(forced || m === 9 || m === 10 || m === 11)) return null;
        return (
          <a href="/sinterklaas-bingo/" data-testid="landing-sint-banner"
             className="block border-b-2 border-black text-white relative overflow-hidden group"
             style={{ background: "linear-gradient(135deg,#9B1B30 0%,#7a1526 55%,#1D2746 100%)" }}>
            <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-center gap-3 text-center flex-wrap">
              <span className="text-xl">🎁</span>
              <span className="font-display font-black text-sm sm:text-base">Nieuw: Sinterklaas plaatjesbingo — kaarten vol vrolijke tekeningen, klaar voor pakjesavond</span>
              <span className="inline-flex items-center gap-1 bg-[var(--brand-yellow,#FFCC00)] text-[#1D2746] font-black text-xs px-3 py-1.5 rounded-full border-2 border-white group-hover:-translate-y-0.5 transition-transform">Bekijk →</span>
            </div>
          </a>
        );
      })()}


      {/* Hero — SEO-focused (2026-08-16): H1 targets "Bingokaarten Generator" /
          "Bingo Card Generator" / "Bingokarten Generator". Cleaner layout —
          eyecatcher banner + inline AI promo pill were removed (user
          feedback: "niet te rommelig"). AI + music tools live in the
          "Popular tools" section further down. */}
      <section
        className="relative overflow-hidden border-b-2 border-black"
        style={{ minHeight: "520px" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-24 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 nb-border bg-[var(--brand-yellow)] px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest nb-shadow-sm" data-testid="hero-badge">
              <Sparkles size={14} /> {t("hero_badge")}
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter mt-5" style={{ letterSpacing: "-0.03em" }} data-testid="hero-h1">
              {t("hero_promise_h1")}
            </h1>
            <p className="mt-4 text-base sm:text-lg lg:text-xl text-neutral-700 max-w-xl font-medium" data-testid="hero-subtitle">
              {t("hero_promise_sub")}
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="hero-benefits">
              {[t("hero_benefit_1"), t("hero_benefit_2"), t("hero_benefit_3")].map((b, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 text-sm font-bold bg-white nb-border px-3 py-1.5 rounded-full">
                  <Check size={14} className="text-[var(--brand-green)]" /> {b}
                </span>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <Link to="/create" className="nb-button bg-[var(--brand-red)] text-white px-6 py-3.5 rounded-md text-base inline-flex items-center gap-2" data-testid="hero-cta-create">
                {t("hero_cta_primary")} <ArrowRight size={18} />
              </Link>
              <a href="#how" className="nb-button bg-white text-black px-6 py-3.5 rounded-md text-base" data-testid="hero-cta-how">
                {t("hero_cta_secondary")}
              </a>
            </div>
            <div className="mt-6 text-sm text-neutral-600 font-medium flex items-center gap-2" data-testid="hero-trust">
              <Lock size={14} /> {t("hero_trust")}
            </div>
          </div>

          {/* Hero visual: joyful theme collage */}
          <div className="lg:col-span-5 relative">
            <div className="relative h-[460px] sm:h-[520px]">
              {/* Background blob */}
              <div className="absolute inset-0 bg-[var(--brand-yellow)] nb-border rounded-3xl rotate-[-3deg]"></div>

              {/* Card 1 - Kerst (back) — real PDF renderer, scaled */}
              <div className="absolute -top-2 right-6 rotate-[7deg] w-[58%]" data-testid="hero-card-kerst">
                <HeroCard designWidth={640}>
                  <BingoCardRender title="Kerst Bingo" size={5} cells={HERO_KERST} theme={HERO_THEME_KERST} freeSpace themeId="kerst" cardNumber={7} />
                </HeroCard>
              </div>

              {/* Card 2 - Sint (mid) */}
              <div className="absolute top-24 left-0 rotate-[-6deg] w-[56%]" data-testid="hero-card-sint">
                <HeroCard designWidth={640}>
                  <BingoCardRender title="Sinterklaas Bingo" size={5} cells={HERO_SINT_PIC} theme={HERO_THEME_SINT} themeId="sinterklaas" cardNumber={3} showCaptions plainBg />
                </HeroCard>
              </div>

              {/* Card 3 - Muziek (front) — showcases logo + brand name + Spotify */}
              <div className="absolute bottom-0 right-0 rotate-[3deg] w-[64%] z-10" data-testid="hero-card-muziek">
                {/* Spotify integration badge — sits on the bottom edge of the
                    music card, overhanging into open space for clear contrast. */}
                <div
                  className="absolute -bottom-4 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-1.5 bg-[#1DB954] text-white nb-border rounded-full pl-1 pr-3 py-1 nb-shadow-sm whitespace-nowrap"
                  data-testid="hero-spotify-badge"
                >
                  <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <svg width="13" height="13" viewBox="0 0 168 168" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#1DB954" d="M83.996.277C37.747.277.253 37.77.253 84.019c0 46.251 37.494 83.741 83.743 83.741 46.254 0 83.744-37.49 83.744-83.741 0-46.246-37.49-83.738-83.745-83.738l.001-.004zm38.404 120.78a5.217 5.217 0 01-7.18 1.73c-19.662-12.01-44.414-14.73-73.564-8.07a5.222 5.222 0 01-6.249-3.93 5.213 5.213 0 013.926-6.25c31.9-7.291 59.263-4.15 81.337 9.34 2.46 1.51 3.24 4.72 1.73 7.18zm10.25-22.805c-1.89 3.075-5.91 4.045-8.98 2.155-22.51-13.839-56.823-17.846-83.448-9.764-3.453 1.043-7.1-.903-8.148-4.35a6.538 6.538 0 014.354-8.143c30.413-9.228 68.222-4.758 94.072 11.127 3.07 1.89 4.04 5.91 2.15 8.976v-.001zm.88-23.744c-26.99-16.031-71.52-17.505-97.289-9.684-4.138 1.255-8.514-1.081-9.768-5.219a7.835 7.835 0 015.221-9.771c29.581-8.98 78.756-7.245 109.83 11.202a7.823 7.823 0 012.74 10.733c-2.2 3.722-7.02 4.949-10.73 2.739z"/>
                    </svg>
                  </span>
                  <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wide">{t("hero_spotify_badge")}</span>
                </div>
                <HeroCard designWidth={680}>
                  <BingoCardRender title="Muziek Bingo" size={5} cells={HERO_MUZIEK} theme={HERO_THEME_MUZIEK} freeSpace themeId="muziek" cardNumber={1} logo={SAMPLE_LOGO} logoSize={64} brandName="Café De Kroon" />
                </HeroCard>
              </div>

              {/* Confetti accents */}
              <div className="absolute top-0 left-1/3 w-3 h-3 bg-[var(--brand-red)] nb-border rotate-45"></div>
              <div className="absolute bottom-4 left-12 w-2 h-2 bg-[var(--brand-blue)] nb-border"></div>
              <div className="absolute top-20 right-2 w-3 h-8 bg-[var(--brand-green)] nb-border rotate-[20deg]"></div>

              {/* Hand-drawn bingo balls floating around */}
              <BingoBall letter="B" number={7} color="red" size={70} rotate={-12} className="absolute -top-4 -left-4 drop-shadow-xl" />
              <BingoBall letter="I" number={23} color="yellow" size={56} rotate={18} className="absolute top-8 right-0 drop-shadow-lg" />
              <BingoBall letter="N" number={42} color="green" size={64} rotate={-7} className="absolute bottom-12 -left-6 drop-shadow-xl" />
              <BingoBall letter="G" number={56} color="blue" size={50} rotate={22} className="absolute -bottom-2 left-1/2 drop-shadow-lg" />
              <BingoBall letter="O" number={66} color="purple" size={58} rotate={-15} className="absolute top-1/2 -right-4 drop-shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* === SmartFindBar — the primary conversion widget (2026-02-28).
           Replaces the previous 4 hero-tiles with a single intelligent input
           that routes free-text queries directly to the matching landing.
           If nothing matches → AI fallback. Handles NL/EN/DE. === */}
      <SmartFindBar />

      {/* === Bingo-caller promo banner — advertises the free premium caller
           (second screen / caller voice / 20 languages) with a link to /caller. === */}
      {callerBanner && (
      <section className="border-b-2 border-black relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0C1A2B 0%, #13263d 100%)" }} data-testid="landing-caller-banner">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[var(--brand-yellow)]/10 blur-3xl -translate-y-1/3 translate-x-1/4 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10 relative">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            {/* Text + CTA */}
            <div className="lg:col-span-7 text-white">
              <div className="inline-block bg-[var(--brand-yellow)] text-black text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded mb-3" data-testid="landing-caller-badge">
                🎱 {callerBanner.badge}
              </div>
              <h2 className="font-display font-black text-xl sm:text-2xl lg:text-3xl leading-tight mb-2 tracking-tight" data-testid="landing-caller-h">
                {callerBanner.h}
              </h2>
              <p className="text-sm sm:text-base opacity-85 mb-4 max-w-xl" data-testid="landing-caller-p">
                {callerBanner.p}
              </p>
              <Link to="/caller" className="nb-button bg-[var(--brand-yellow)] text-black px-6 py-3 rounded-md font-black text-sm sm:text-base inline-flex items-center gap-2 whitespace-nowrap" data-testid="landing-caller-cta">
                {callerBanner.cta} <ArrowRight size={16} />
              </Link>
            </div>
            {/* Feature list */}
            <div className="lg:col-span-5">
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm font-bold text-white/90" data-testid="landing-caller-features">
                <li>📺 {callerBanner.f1}</li>
                <li>🔊 {callerBanner.f2}</li>
                <li>🌍 {callerBanner.f3}</li>
                <li>🎲 {callerBanner.f4}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Honest trust strip — factual signals only (no invented reviews or
          counts). Social-proof/testimonials structure comes in FASE D. */}
      <section className="border-b-2 border-black bg-white" data-testid="trust-strip">
        <div className="max-w-6xl mx-auto px-4 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[t("trust_fact_1"), t("trust_fact_2"), t("trust_fact_3"), t("trust_fact_4")].map((f, i) => (
            <div key={i} className="flex items-center justify-center gap-2 text-sm font-bold text-neutral-800 text-center">
              <Check size={16} className="text-[var(--brand-green)] flex-shrink-0" /> {f}
            </div>
          ))}
        </div>
      </section>

      {/* Small link to the comparison page — internal-link push for GEO */}
      {lang === "nl" && (
        <section className="border-b-2 border-black bg-white">
          <div className="max-w-5xl mx-auto px-4 py-3 text-center">
            <a
              href="/beste-bingo-generator-2026/"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-neutral-700 hover:text-[var(--brand-red)] underline underline-offset-4"
              data-testid="landing-compare-link"
            >
              📊 Vergelijk bingocards-generator met 14 andere bingo-tools →
            </a>
          </div>
        </section>
      )}

      {/* Why-BingoMaker USP block — GEO step 5, 2026-08-17 */}
      <section className="border-b-2 border-black bg-[var(--bg-primary)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
          <WhyBingoMaker />
        </div>
      </section>

      <HowToBingo lang={lang} />



      {/* === Music Bingo callout — moved up right after hero === */}
      <section className="border-b-2 border-black bg-[var(--brand-yellow)] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-[var(--brand-red)] opacity-15 blur-3xl -translate-y-1/3 translate-x-1/4 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 relative">
          <div className="grid lg:grid-cols-12 gap-6 sm:gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 nb-border bg-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest nb-shadow-sm mb-4">
                🎵 {t("nav_music_bingo")}
              </div>
              <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tight mb-3">
                {t("mb_landing_h1_a")} <span className="text-[var(--brand-red)]">{t("mb_landing_h1_b")}</span>
              </h2>
              <p className="text-base sm:text-lg text-black/80 mb-5 font-medium max-w-xl">{t("mb_landing_p")}</p>
              {/* Direct link to the language-appropriate static SEO page.
                  Previously pointed to /music-bingo (React route) but that
                  route now hard-redirects — better to jump straight to
                  the rich content page users end up on anyway. */}
              <a
                href={{ nl: "/nl/muziek-bingo/", en: "/en/music-bingo/", de: "/de/musik-bingo/" }[lang] || "/nl/muziek-bingo/"}
                className="inline-flex items-center gap-2 nb-button bg-black text-white px-6 py-3 rounded-md font-bold text-sm sm:text-base"
                data-testid="landing-music-bingo-cta"
              >
                {t("mb_landing_cta")} →
              </a>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-3 gap-2">
                {["80s", "90s", "nederlandstalig", "duitstalig", "rock", "foute_muziek"].map((id, i) => {
                  const colors = ["#9C27B0", "#00838F", "#FF6F00", "#D32F2F", "#212121", "#EC407A"];
                  const labels = {
                    "80s": "80s", "90s": "90s",
                    nederlandstalig: { nl: "Nederlandstalig", en: "Dutch", de: "NL Hits" },
                    duitstalig: { nl: "Duitstalig", en: "German", de: "Deutsch" },
                    rock: { nl: "Rock", en: "Rock", de: "Rock" },
                    foute_muziek: { nl: "Foute Muziek", en: "Cheesy", de: "Foute" },
                  };
                  const label = typeof labels[id] === "string" ? labels[id] : (labels[id][lang] || labels[id].en);
                  return (
                    <Link
                      key={id}
                      to={`/create?theme=muziek&genre=${id}`}
                      className="aspect-square nb-border rounded-md flex items-center justify-center text-center font-display font-black text-sm sm:text-base hover:translate-x-[-1px] hover:translate-y-[-1px] transition-transform p-2"
                      style={{ backgroundColor: colors[i], color: "#fff" }}
                      data-testid={`landing-music-genre-${id}`}
                    >
                      {label}
                    </Link>
                  );
                })}
              </div>
              <p className="text-center text-xs text-black/60 mt-3 font-bold">{t("mb_landing_genres_more")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* === Spotify differentiator banner — 2026-08-16.
           Prominently placed after the music bingo callout so visitors see
           that we're the only bingo tool with live Spotify Web Playback. === */}
      <section
        className="border-b-2 border-black relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #7C3AED 0%, #2a0f5e 100%)" }}
        data-testid="landing-spotify-banner"
      >
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/10 blur-3xl -translate-y-1/3 translate-x-1/4 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14 relative">
          <div className="grid lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-2 flex justify-center lg:justify-start">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#191414] border-2 border-black flex items-center justify-center nb-shadow-lg">
                <Music2 size={48} className="text-[var(--brand-yellow)]" strokeWidth={2.5} />
              </div>
            </div>
            <div className="lg:col-span-7 text-white">
              <div className="inline-block bg-[#191414] text-[var(--brand-yellow)] text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded mb-3" data-testid="landing-spotify-badge">
                🎵 {t("hero_spotify_badge")}
              </div>
              <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl leading-tight mb-3 tracking-tight" data-testid="landing-spotify-h">
                {t("spotify_banner_h")}
              </h2>
              <p className="text-base sm:text-lg opacity-95 mb-4 max-w-2xl" data-testid="landing-spotify-p">
                {t("spotify_banner_p")}
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm font-bold opacity-95" data-testid="landing-spotify-features">
                <li>✓ {t("spotify_feat_1")}</li>
                <li>✓ {t("spotify_feat_2")}</li>
                <li>✓ {t("spotify_feat_3")}</li>
                <li>✓ {t("spotify_feat_4")}</li>
              </ul>
            </div>
            <div className="lg:col-span-3 flex flex-col gap-2 items-start lg:items-end">
              <button
                onClick={() => { window.location.href = "/create?theme=muziek"; }}
                className="nb-button bg-[var(--brand-yellow)] text-black px-6 py-3 rounded-md font-black text-sm sm:text-base inline-flex items-center gap-2 whitespace-nowrap"
                data-testid="landing-spotify-cta"
              >
                {t("spotify_banner_cta")} <ArrowRight size={16} />
              </button>
              <a
                href={{ nl: "/nl/muziek-bingo/", en: "/en/music-bingo/", de: "/de/musik-bingo/" }[lang]}
                className="text-white/90 text-xs font-bold underline hover:text-[var(--brand-yellow)] hover:no-underline"
                data-testid="landing-spotify-more"
              >
                {t("spotify_banner_more")} →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* === Honest use-case trust section (FASE D) — factual occasions,
           no fabricated reviews. Placed just before pricing to build
           relatable context before the price. === */}
      <section className="border-b-2 border-black bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-20">
          <UseCases />
        </div>
      </section>

      {/* === Pricing comparison — Free / Pro / Pro+ — clear USPs, no
           duplicate info elsewhere on the page. Moved up here so visitors
           immediately see what each tier offers. === */}
      <section className="border-b-2 border-black bg-[var(--bg-primary)]" data-testid="pricing-comparison">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-20">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 nb-border bg-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest nb-shadow-sm mb-3">
              {t("prices")}
            </div>
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tight mb-3">
              {t("pricing_title")}
            </h2>
            <p className="text-base sm:text-lg text-neutral-700 max-w-2xl mx-auto font-medium">
              {t("pricing_subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-5 max-w-3xl mx-auto items-stretch">
            {/* FREE */}
            <div className="nb-card rounded-md p-6 bg-white flex flex-col" data-testid="pricing-free">
              <div className="font-display font-bold text-xl mb-1 flex items-center gap-2">
                {t("free_plan")}
                <span className="text-[10px] nb-border bg-white rounded-full px-2 py-0.5 font-bold uppercase tracking-widest">{t("always")}</span>
              </div>
              <div className="font-display font-black text-5xl mb-1">€0</div>
              <p className="text-sm text-neutral-600 font-medium mb-4">{t("try_no_account")}</p>
              <ul className="space-y-2 text-sm mb-5 flex-1">
                <FeatureRow yes>{t("free_f1")}</FeatureRow>
                <FeatureRow yes>{t("free_f2")}</FeatureRow>
                <FeatureRow yes>{t("free_f3")}</FeatureRow>
                <FeatureRow yes>{t("free_f4")}</FeatureRow>
                <FeatureRow yes>{t("ai_free_bullet")}</FeatureRow>
                <FeatureRow>{t("locked_f1")}</FeatureRow>
                <FeatureRow>{t("locked_f2")}</FeatureRow>
                <FeatureRow>{t("locked_f3")}</FeatureRow>
              </ul>
              <Link to="/create" className="nb-button bg-black text-white px-5 py-3 rounded-md font-bold text-sm inline-flex items-center justify-center gap-1.5" data-testid="pricing-free-cta">
                {t("cta_start")} <ArrowRight size={14} />
              </Link>
            </div>

            {/* PRO */}
            <div className="rounded-md p-6 bg-[var(--brand-red)] text-white nb-border nb-shadow-lg relative flex flex-col" data-testid="pricing-pro">
              <div className="absolute -top-3 right-4 bg-[var(--brand-yellow)] text-black nb-border px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest">
                {t("popular")}
              </div>
              <div className="font-display font-bold text-xl mb-1 flex items-center gap-2">
                <Crown size={18} /> {t("paid_plan")}
              </div>
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className="font-display font-black text-2xl opacity-90">{t("from_price")}</span>
                <span className="font-display font-black text-5xl">€9,95</span>
              </div>
              <p className="text-sm opacity-90 font-medium mb-2">{t("per_session")}</p>
              {/* Staffel — 3 tiers displayed as compact table */}
              <div className="text-xs bg-black/25 rounded-md p-3 mb-3 space-y-1 font-semibold">
                <div className="flex justify-between"><span>{t("tier_upto")} 50 {t("tier_cards")}</span><span>€9,95</span></div>
                <div className="flex justify-between"><span>{t("tier_upto")} 100 {t("tier_cards")}</span><span>€14,95</span></div>
                <div className="flex justify-between"><span>{t("tier_unlimited")}</span><span>€19,95</span></div>
              </div>
              <ul className="space-y-2 text-sm mb-5 flex-1">
                <FeatureRow yes light><b>{t("pro_f3")}</b></FeatureRow>
                <FeatureRow yes light><b>{t("pro_f4")}</b></FeatureRow>
                <FeatureRow yes light><b>{t("pro_f5")}</b></FeatureRow>
                <FeatureRow yes light><b>{t("pro_f6")}</b></FeatureRow>
                <FeatureRow yes light>{t("pro_f7")}</FeatureRow>
                <FeatureRow yes light>{t("pro_f8")}</FeatureRow>
                <FeatureRow yes light><b>{t("pro_f_music1")}</b></FeatureRow>
                <FeatureRow yes light>{t("pro_f_music2")}</FeatureRow>
                <FeatureRow yes light><b>{t("ai_pro_bullet")}</b></FeatureRow>
              </ul>
              <button
                onClick={handleActivatePro}
                className="nb-button bg-black text-white px-5 py-3 rounded-md font-bold text-sm inline-flex items-center justify-center gap-1.5"
                data-testid="pricing-pro-cta"
              >
                {t("tier_pro_cta")} <ArrowRight size={14} />
              </button>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-4xl mx-auto" data-testid="pricing-trust-strip">
            {[t("pay_trust_1"), t("pay_trust_2"), t("pay_trust_3"), t("pay_trust_4")].map((f, i) => (
              <div key={i} className="flex items-center justify-center gap-2 text-xs sm:text-sm font-bold text-neutral-800 text-center nb-border bg-white rounded-md px-3 py-2.5">
                <Lock size={14} className="text-[var(--brand-green)] flex-shrink-0" /> {f}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b-2 border-black bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-10">
            <h2 className="lg:col-span-7 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {t("features_title")}
            </h2>
            <p className="lg:col-span-5 text-neutral-700 text-base">
              {t("features_subtitle")}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="nb-card rounded-md p-5" data-testid={`feature-${f.title}`}>
                <div className="w-11 h-11 nb-border rounded-md bg-[var(--brand-yellow)] flex items-center justify-center mb-4 nb-shadow-sm">
                  <f.icon size={20} strokeWidth={2.4} />
                </div>
                <div className="font-display font-bold text-xl mb-1">{f.title}</div>
                <div className="text-sm text-neutral-700 font-medium">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === Mobile / desktop play preview — replaces the small theme
           showcase. Shows actual gameplay (player / share / host) so users
           understand the digital flow before scrolling further. === */}
      <section className="border-b-2 border-black bg-[var(--bg-highlight)]" data-testid="landing-mobile-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-20">
          <MobilePlayPreview />
        </div>
      </section>

      {/* How it works section removed 2026-02-24 — HowToBingo above (line 244)
          already covers the same steps with richer HowTo schema. */}

      {/* (Pro+ teaser moved above — see line ~209) */}

      {/* Quick tools cross-promo: AI Examples Gallery.
          (The "Bingo Number Caller" card was removed — the caller is now
          promoted in the top banner; its SEO link lives in the footer.) */}
      <section className="border-b-2 border-black bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16">
          <div className="grid grid-cols-1 gap-6 lg:gap-8">
            <a
              href={lang === "en" ? "/en/examples/" : (lang === "de" ? "/de/beispiele/" : "/nl/voorbeelden/")}
              className="nb-card rounded-2xl p-6 lg:p-8 transition-transform hover:translate-x-[-2px] hover:translate-y-[-2px]"
              style={{
                background: "linear-gradient(135deg, #FFF8E1 0%, #FFE082 100%)",
              }}
              data-testid="cross-promo-examples"
            >
              <div className="inline-flex items-center gap-1.5 bg-black text-[var(--brand-yellow)] text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded mb-3">
                {t("ai_badge")}
              </div>
              <h3 className="font-display font-black text-2xl sm:text-3xl mb-2 leading-tight">
                {t("cross_examples_h")}
              </h3>
              <p className="text-sm sm:text-base text-neutral-700 mb-4 leading-snug">
                {t("cross_examples_p")}
              </p>
              <div className="font-display font-black text-[var(--brand-red)] inline-flex items-center gap-1">
                {t("cross_examples_cta")} <span aria-hidden>→</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      <section className="border-b-2 border-black bg-[var(--bg-primary)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 nb-border bg-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest nb-shadow-sm mb-3">
              {t("themes_section_badge")}
            </div>
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tight">
              {t("themes_section_h_a")} <span className="text-[var(--brand-red)]">{t("themes_section_h_b")}</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-700 mt-3 max-w-2xl mx-auto font-medium">{t("themes_section_p")}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="landing-themes-grid">
            {THEME_PAGES.map((th) => (
              <a
                key={th.id}
                href={themePageUrl(th, lang)}
                className="group nb-card rounded-md p-4 sm:p-5 hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform flex flex-col items-start gap-2"
                style={{ backgroundColor: th.bg }}
                data-testid={`landing-theme-${th.id}`}
              >
                <div className="text-3xl sm:text-4xl mb-1">{th.emoji}</div>
                <div className="font-display font-black text-lg sm:text-xl tracking-tight" style={{ color: th.color }}>
                  {th.labelI18n[lang] || th.labelI18n.en}
                </div>
                <div className="text-xs font-bold uppercase tracking-widest opacity-60 group-hover:opacity-100 transition-opacity" style={{ color: th.color }}>
                  {t("themes_section_view")} →
                </div>
              </a>
            ))}
          </div>

          {/* Music sub-genres discovery row — 6 popular Spotify-ready genres
              as quick-links. Foute variants prominently marked. 2026-08-16. */}
          <div className="mt-10 pt-8 border-t-2 border-dashed border-black/20" data-testid="landing-music-subgenres">
            <div className="flex items-center justify-center gap-2 mb-5 flex-wrap">
              <div className="inline-flex items-center gap-2 bg-[#7C3AED] text-white px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest nb-border">
                🎵 {t("hero_spotify_badge")}
              </div>
              <h3 className="font-display font-black text-2xl sm:text-3xl">
                {lang === "en" ? "Or pick a music sub-genre" : lang === "de" ? "Oder wähl ein Musik-Sub-Genre" : "Of kies een muziek sub-genre"}
              </h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
              {[
                { id: "foute_kerst", emoji: "🎄", nl: "Foute Kerst Hits", en: "Christmas Hits", de: "Weihnachtshits", href: "/nl/muziek-bingo/foute-kerst-hits/", bg: "#F2F8F2", accent: "#0E5C2F" },
                { id: "foute_bruiloft", emoji: "💍", nl: "Foute Bruiloft Hits", en: "Wedding Hits", de: "Hochzeits-Hits", href: "/nl/muziek-bingo/foute-hits-bruiloft/", bg: "#FFF8F0", accent: "#9C7B3B" },
                { id: "foute_bedrijf", emoji: "🥂", nl: "Foute Bedrijfsfeest", en: "Office Party Hits", de: "Firmenfeier-Hits", href: "/nl/muziek-bingo/foute-hits-bedrijfsfeest/", bg: "#F3E5F5", accent: "#6A1B9A" },
                { id: "80s", emoji: "🎸", nl: "Jaren 80 Hits", en: "80s Hits", de: "80er Hits", href: lang === "nl" ? "/nl/muziek-bingo/jaren-80/" : lang === "de" ? "/de/musik-bingo/80er/" : "/en/music-bingo/80s/", bg: "#FFF3E0", accent: "#EF6C00" },
                { id: "90s", emoji: "🎧", nl: "Jaren 90 Hits", en: "90s Hits", de: "90er Hits", href: lang === "nl" ? "/nl/muziek-bingo/jaren-90/" : lang === "de" ? "/de/musik-bingo/90er/" : "/en/music-bingo/90s/", bg: "#E3F2FD", accent: "#1976D2" },
                { id: "foute", emoji: "🎉", nl: "Foute Muziek", en: "Guilty Pleasures", de: "Guilty Pleasures", href: lang === "nl" ? "/nl/muziek-bingo/foute-muziek/" : lang === "de" ? "/de/musik-bingo/guilty-pleasures/" : "/en/music-bingo/guilty-pleasures/", bg: "#FCE4EC", accent: "#EC407A" },
              ].map((g) => (
                <a
                  key={g.id}
                  href={g.href}
                  className="group nb-card rounded-md p-3 sm:p-4 hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform flex flex-col items-start gap-1 relative"
                  style={{ backgroundColor: g.bg }}
                  data-testid={`landing-music-sub-${g.id}`}
                >
                  <div className="text-2xl sm:text-3xl mb-1">{g.emoji}</div>
                  <div className="font-display font-black text-sm sm:text-base leading-tight" style={{ color: g.accent }}>
                    {g[lang] || g.en}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRO Highlight + duplicate bottom pricing removed — comparison moved up. */}

      <footer className="bg-white border-t-2 border-black py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row flex-wrap items-center justify-between gap-3 text-sm text-neutral-700">
          <div className="flex items-center gap-2 font-display font-bold">
            <Dices size={18} strokeWidth={2.5} className="text-[var(--brand-red)]" />
            <a href="https://bingocards-generator.com" className="hover:underline" data-testid="footer-domain">bingocards-generator.com</a>
          </div>
          <nav className="flex items-center gap-4 text-sm flex-wrap justify-center">
            <Link to="/about" data-testid="footer-about" className="hover:underline font-bold">About</Link>
            <Link to="/contact" data-testid="footer-contact" className="hover:underline font-bold">Contact</Link>
            <Link to="/caller" data-testid="footer-caller" className="hover:underline font-bold">Bingo caller</Link>
            {lang === "nl" && (
              <a href="/beste-bingo-generator-2026/" data-testid="footer-compare" className="hover:underline font-bold">
                {t("footer_compare")}
              </a>
            )}
            <Link to="/privacy" data-testid="footer-privacy" className="hover:underline font-bold">Privacy</Link>
          </nav>
          <a
            href="mailto:info@bingocards-generator.com"
            className="inline-flex items-center gap-2 nb-button bg-white text-black border-2 border-black px-4 py-2 rounded-md font-bold hover:bg-[var(--brand-yellow)] transition-colors"
            data-testid="footer-mailto"
          >
            <Mail size={16} /> info@bingocards-generator.com
          </a>
          <div className="text-xs text-neutral-500">© {new Date().getFullYear()} bingocards-generator.com</div>
        </div>
      </footer>

      <ProPlusModal open={showProPlus} onClose={() => setShowProPlus(false)} />
      <PaywallModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        usage={{ free_limit: 20 }}
        onUnlocked={() => setShowPaywall(false)}
      />
    </div>
  );
}

/**
 * HowToBingo — visible on-page HowTo section + JSON-LD schema.
 *
 * Purpose: bootstrap Google AI Overview (SGE) inclusion for the query
 * "bingo kaarten maken" / "make bingo cards" / "Bingokarten erstellen".
 * Google's AI Overview strongly prefers sources that:
 *   1. Rank in the top ~10 for the query
 *   2. Have HowTo schema with clear numbered steps
 *   3. Answer the question directly at the top of the page
 *
 * Placed immediately after the hero so the schema + visible content sit
 * high in the DOM (crawler-friendly). NL is the primary market; EN/DE
 * localised via inline COPY dict to avoid bloating the central i18n
 * bundle for a static block.
 */
function HowToBingo({ lang }) {
  const COPY = {
    nl: {
      h1: "Hoe maak je een bingokaart?",
      lead: "Een bingokaart maken kan gratis en online — in minder dan 60 seconden. Zo werkt het bij bingocards-generator.com:",
      steps: [
        { name: "Kies je thema en formaat", text: "Kies uit 15+ voorgevulde thema's (Kerst, Muziek, Sinterklaas, Klassiek en meer) of start met een leeg raster. Selecteer 3x3, 4x4 of 5x5." },
        { name: "Vul je bingokaart met tekst of afbeeldingen", text: "Type je eigen woorden of gebruik de 80+ thema-suggesties. Wil je foto's? Upload ze direct van je telefoon of laptop." },
        { name: "Kies aantal kaarten en print-layout", text: "Maak 1 tot 100+ unieke kaarten in één keer. Kies 1, 2, 3 of 4 kaarten per A4-pagina voor de PDF." },
        { name: "Download de PDF of speel online", text: "Klik op download voor een print-klare PDF óf op 'Opslaan om online mee te spelen' voor een deel-code die je vrienden kunnen openen." },
      ],
      tip_h: "Snel klaar zijn?",
      tip: "Klik op 'Willekeurig vullen' om alle vakjes in één klik met thema-woorden te vullen — perfect als het bijna feest is en je nog geen bingokaart hebt.",
      faq_h: "Veelgestelde vragen",
      faq: [
        { q: "Kan ik bingokaarten gratis maken?", a: "Ja. Je maakt elke dag gratis 1 bingo (tot 20 kaarten), inclusief PDF-download — geen account nodig, geen creditcard vereist. Wil je meerdere bingo's per dag, online meespelen (deel-code + presentator-modus) of grotere aantallen (50+)? Dat zit in PRO: eenmalig vanaf €9,95 per sessie, geen abonnement." },
        { q: "Kan ik afbeeldingen gebruiken op mijn bingokaart?", a: "Zeker. Upload eigen foto's direct in de vakjes — handig voor bijvoorbeeld een kinderfeestje (foto's van familieleden), bruiloft (herinneringen) of klasgroep (leerling-foto's)." },
        { q: "Hoe maak ik een unieke bingokaart voor iedereen?", a: "Zet 'aantal kaarten' op het gewenste getal en de generator randomiseert de posities voor elke kaart automatisch. Zo krijgt iedereen dezelfde items maar op een andere plek — geen 2 kaarten identiek." },
        { q: "Kan ik online bingo spelen met vrienden?", a: "Ja — dit is een PRO-functie. Na het opslaan deel je een QR-/deel-code: iedereen speelt op zijn eigen telefoon en streept zelf af. Als host toon je de items in de presentator-modus. Automatisch nummers trekken, Spotify live afspelen én automatisch op bingo controleren zit in PRO+." },
        { q: "Werkt het ook voor muziek bingo?", a: "Ja. Kies het Muziek-thema voor kant-en-klare bingokaarten met nummers — gratis te maken en te printen. Wil je automatisch een Spotify-playlist laten aanmaken en nummers live in je browser afspelen tijdens het spel? Dat is de PRO+ Spotify-optie." },
      ],
      cta: "Start nu — 1 gratis bingo per dag",
    },
    en: {
      h1: "How to make a bingo card",
      lead: "You can make bingo cards online for free — in under 60 seconds. Here's how it works at bingocards-generator.com:",
      steps: [
        { name: "Pick a theme and format", text: "Choose from 15+ pre-filled themes (Christmas, Music, Classic, Nature and more) or start blank. Select 3x3, 4x4 or 5x5 grid." },
        { name: "Fill your bingo card with text or images", text: "Type your own words or use the 80+ theme suggestions. Want photos? Upload them straight from your phone or laptop." },
        { name: "Pick card count and print layout", text: "Generate 1 to 100+ unique cards in one go. Choose 1, 2, 3 or 4 cards per A4 page for the PDF." },
        { name: "Download the PDF or play online", text: "Click download for a print-ready PDF, or 'Save to play online' for a shareable code your friends can open on their phones." },
      ],
      tip_h: "Need to be fast?",
      tip: "Click 'Random fill' to fill every cell with theme words in a single click — perfect when the party is almost starting.",
      faq_h: "Frequently asked questions",
      faq: [
        { q: "Can I make bingo cards for free?", a: "Yes. You can make 1 free bingo every day (up to 20 cards), including PDF download — no account, no credit card needed. Want more than one bingo per day, online play (share-code + presenter mode) or larger batches (50+)? That's part of PRO: a one-time payment from €9.95 per session, no subscription." },
        { q: "Can I use images on my bingo card?", a: "Absolutely. Upload your own photos directly into the cells — great for kids' parties (family photos), weddings (memories) or classrooms (student pictures)." },
        { q: "How do I make a unique bingo card for every player?", a: "Set the card count to whatever number you need and the generator randomises the positions per card automatically. Everyone gets the same items but in different places — no two cards identical." },
        { q: "Can I play bingo online with friends?", a: "Yes — this is a PRO feature. After saving you share a QR/share-code: each player plays on their own phone and marks their own card. As host you show the items in presenter mode. Automatic number drawing, live Spotify playback and automatic bingo-checking are part of PRO+." },
        { q: "Does it work for music bingo?", a: "Yes. Pick the Music theme for ready-made bingo cards with songs — free to create and print. Want a Spotify playlist created automatically and tracks played live in your browser during the game? That's the PRO+ Spotify option." },
      ],
      cta: "Start now — 1 free bingo per day",
    },
    de: {
      h1: "Wie erstelle ich eine Bingokarte?",
      lead: "Du kannst Bingokarten kostenlos online erstellen — in weniger als 60 Sekunden. So funktioniert es bei bingocards-generator.com:",
      steps: [
        { name: "Wähle Thema und Format", text: "Wähle aus 15+ vorgefüllten Themen (Weihnachten, Musik, Klassik, Natur und mehr) oder starte leer. Wähle 3x3, 4x4 oder 5x5." },
        { name: "Fülle deine Karte mit Text oder Bildern", text: "Tippe eigene Wörter oder nutze die 80+ Themenvorschläge. Willst du Fotos? Lade sie direkt vom Handy oder Laptop hoch." },
        { name: "Wähle Kartenanzahl und Druck-Layout", text: "Erstelle 1 bis 100+ eindeutige Karten auf einmal. Wähle 1, 2, 3 oder 4 Karten pro A4-Seite für die PDF." },
        { name: "Lade die PDF herunter oder spiele online", text: "Klick auf Download für eine druckfertige PDF oder auf 'Speichern zum Online-Spielen' für einen Teilen-Code, den deine Freunde öffnen können." },
      ],
      tip_h: "Musst du schnell fertig sein?",
      tip: "Klick auf 'Zufällig füllen', um alle Felder in einem Klick mit Themenwörtern zu füllen — perfekt wenn das Fest fast anfängt.",
      faq_h: "Häufig gestellte Fragen",
      faq: [
        { q: "Kann ich Bingokarten kostenlos erstellen?", a: "Ja. Du erstellst jeden Tag 1 kostenloses Bingo (bis 20 Karten), inklusive PDF-Download — kein Konto, keine Kreditkarte nötig. Willst du mehrere Bingos pro Tag, Online-Spiel (Teil-Code + Moderator-Modus) oder größere Mengen (50+)? Das gehört zu PRO: einmalig ab €9,95 pro Session, kein Abo." },
        { q: "Kann ich Bilder auf meiner Bingokarte verwenden?", a: "Absolut. Lade eigene Fotos direkt in die Felder — perfekt für Kindergeburtstage (Familienfotos), Hochzeiten (Erinnerungen) oder Klassen (Schülerbilder)." },
        { q: "Wie erstelle ich für jeden Spieler eine eindeutige Karte?", a: "Setze die Kartenanzahl auf die gewünschte Zahl und der Generator randomisiert die Positionen automatisch. Jeder bekommt die gleichen Items, aber an anderen Stellen — keine zwei Karten identisch." },
        { q: "Kann ich online Bingo mit Freunden spielen?", a: "Ja — das ist eine PRO-Funktion. Nach dem Speichern teilst du einen QR-/Teil-Code: jeder Spieler spielt auf seinem eigenen Handy und markiert selbst. Als Host zeigst du die Items im Moderator-Modus. Automatisches Ziehen, Live-Spotify-Wiedergabe und automatischer Bingo-Check gehören zu PRO+." },
        { q: "Funktioniert es auch für Musik-Bingo?", a: "Ja. Wähle das Musik-Thema für fertige Bingokarten mit Songs — kostenlos zu erstellen und zu drucken. Du willst automatisch eine Spotify-Playlist erstellen und Songs live im Browser abspielen? Das ist die PRO+ Spotify-Option." },
      ],
      cta: "Jetzt starten — 1 gratis Bingo pro Tag",
    },
  };
  const c = COPY[lang] || COPY.en;

  // HowTo JSON-LD — Google's AI Overview + Rich Results specifically look
  // for this structure to feature step-by-step content.
  const howToLd = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": c.h1,
    "description": c.lead,
    "totalTime": "PT1M",
    "estimatedCost": {"@type": "MonetaryAmount", "currency": "EUR", "value": "0"},
    "tool": [{"@type": "HowToTool", "name": "bingocards-generator.com"}],
    "step": c.steps.map((s, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "name": s.name,
      "text": s.text,
      "url": `https://bingocards-generator.com/#step-${i + 1}`,
    })),
  };

  // FAQPage JSON-LD — the exact queries a user types into Google become
  // the questions. Google may show these as expandable rich results.
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": c.faq.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {"@type": "Answer", "text": f.a},
    })),
  };

  return (
    <section
      id="how"
      className="border-b-2 border-black bg-white"
      data-testid="howto-section"
      aria-labelledby="howto-heading"
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(howToLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-20">
        <h2
          id="howto-heading"
          className="font-display font-black text-3xl sm:text-5xl tracking-tight mb-4"
          data-testid="howto-heading"
        >
          {c.h1}
        </h2>
        <p className="text-lg text-neutral-800 mb-8" data-testid="howto-lead">{c.lead}</p>

        <ol className="space-y-4 mb-10" data-testid="howto-steps">
          {c.steps.map((s, i) => (
            <li
              key={i}
              id={`step-${i + 1}`}
              className="flex gap-4 p-5 border-2 border-black rounded-xl bg-[var(--bg-primary)]"
              data-testid={`howto-step-${i + 1}`}
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--brand-red)] text-white flex items-center justify-center font-display font-black text-lg">
                {i + 1}
              </div>
              <div>
                <div className="font-display font-black text-lg mb-1">{s.name}</div>
                <p className="text-neutral-800">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="p-5 bg-[var(--brand-yellow)] border-2 border-black rounded-xl mb-12" data-testid="howto-tip">
          <div className="font-display font-black text-lg mb-1">💡 {c.tip_h}</div>
          <p>{c.tip}</p>
        </div>

        <h3
          className="font-display font-black text-2xl sm:text-3xl mb-5"
          data-testid="howto-faq-heading"
        >
          {c.faq_h}
        </h3>
        <div className="space-y-3 mb-10">
          {c.faq.map((f, i) => (
            <details
              key={i}
              className="border-2 border-black rounded-xl bg-white overflow-hidden group"
              data-testid={`howto-faq-${i}`}
            >
              <summary className="p-4 font-display font-bold cursor-pointer hover:bg-[var(--brand-yellow)] transition-colors">
                {f.q}
              </summary>
              <div className="p-4 pt-0 text-neutral-700 border-t-2 border-black bg-[var(--bg-primary)]">
                {f.a}
              </div>
            </details>
          ))}
        </div>

        <div className="flex justify-center">
          <Link
            to="/create"
            className="nb-button bg-[var(--brand-red)] text-white px-8 py-4 rounded-md font-black text-lg inline-flex items-center gap-2"
            data-testid="howto-cta"
          >
            {c.cta} <ArrowRight size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
}


function FeatureRow({ yes, light, green, children }) {
  const tickColor = green ? "bg-[#1DB954] text-white" : "bg-[var(--brand-green)] text-white";
  return (
    <li className="flex items-center gap-2">
      <span className={`w-5 h-5 nb-border rounded-full flex items-center justify-center flex-shrink-0 ${yes ? tickColor : "bg-white text-neutral-500"}`}>
        {yes ? <Check size={13} strokeWidth={3.5} /> : <X size={13} strokeWidth={3.5} />}
      </span>
      <span className={light ? "text-white" : "text-neutral-800"}>{children}</span>
    </li>
  );
}
